"use client";

import { useEffect, useState, useMemo, createContext, useContext } from "react";
import {
  getGeoLocation,
  type GeoLocation,
} from "@marketing-sites/analytics/get-geo-location";

type UserLocation = GeoLocation;

const LocationContext = createContext<{
  country?: string;
  countryCode?: string;
  region?: string;
  isOptInCountry?: boolean;
}>({
  isOptInCountry: true, // Default to true & set as a fallback, as it's safer to force the user to opt in if the country is unknown
});

export const UserLocationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [location, setLocation] = useState<UserLocation | undefined>(undefined);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        setLocation(await getGeoLocation());
      } catch (err) {
        console.error("Failed to fetch user location", err);
      }
    };

    fetchLocation();
  }, []);

  const contextValue = useMemo(() => {
    const country = location?.country;
    const countryCode = location?.country_code;
    const region = location?.region;

    return {
      country,
      countryCode,
      region,
      isOptInCountry: location?.marketing_consent_required ?? true,
    };
  }, [location]);

  return (
    <LocationContext.Provider value={contextValue}>
      {children}
    </LocationContext.Provider>
  );
};

export const useUserLocation = () => useContext(LocationContext);
